import useMutation from 'hooks/react-query/useMutation';

import { getRevenueProjectById, RevenueProjectStatus } from 'api/v3/revenues';
import { InstallmentTypeEnum, Invoice } from 'types/invoice.types';
import { get, last, omit } from 'lodash';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import { getRevenueById } from 'api/v2/revenues';
import showToast from 'utils/toast';
import { useTranslation } from 'react-i18next';

const adaptRevenueProjectForExtending = (
  invoice: Partial<Invoice>,
): Partial<Invoice> => {
  return omit(invoice, [
    '_id',
    'orderReference',
    'revenueNumber',
    'invoiceDate',
    'dueDate',
    'deliveryDate',
    'paymentDate',
    'deliveryPeriod',
    'items',
    'paymentAmount',
    'baseCurrencyTotalAmountInclVAT',
    'paymentQrCode',
    'deleted',
    'dateSubmitted',
    'accountantReview',
    'created',
    'transactions',
    'paymentType',
    'period',
    'status',
    'taxLock',
    'quoteId',
    'revenueProject',
    // '_matchedProject',
  ]);
};

const useExtendRevenueProject = () => {
  const { t } = useTranslation();
  const navigate = useCustomNavigate();

  const mutation = useMutation((invoiceId: string) =>
    getRevenueById(invoiceId),
  );

  const format = (invoice: Partial<Invoice>): Partial<Invoice> => {
    const matchedProject = invoice._matchedProject;

    const common = adaptRevenueProjectForExtending(invoice);

    if (!matchedProject) return common;

    return {
      ...common,
      revenueProject: {
        installmentAmountShownAs:
          invoice.revenueProject?.installmentAmountShownAs,
        installmentInvoiceType: InstallmentTypeEnum.partial,
        snapshot: {
          projectId: matchedProject?._id,
          revenues: matchedProject?.revenues
            .filter((r) => r.revenueId)
            .map((r) => ({
              ...r,
              revenueId: r.revenueId!._id,
              status: r.revenueId!.status,
              dueDate: r.revenueId!.dueDate,
            })),
          items: matchedProject?.items,
          currency: matchedProject?.currency,
          baseCurrency: matchedProject?.baseCurrency,
        },
      },
    };
  };

  const process = (invoice: Partial<Invoice>) => {
    if (!invoice._matchedProject) {
      showToast(
        {
          // default case, doesn't need to be translated
          title: `Invoice ${invoice.revenueNumber} does not have a matched project`,
        },
        { type: 'error' },
      );
      return;
    }

    const isProjectComplete =
      invoice._matchedProject?.status === RevenueProjectStatus.completed;

    if (isProjectComplete) {
      showToast(
        {
          title: t('installment_project.extend.complete.error', {
            revenueNumber:
              last(invoice._matchedProject?.revenues)?.revenueNumber || '-',
          }),
        },
        { type: 'error' },
      );
      return;
    }

    navigate('/revenues/create', {
      state: {
        guessedDocument: format(invoice),
      },
    });
  };

  async function run(invoiceId: string): Promise<void>;
  async function run(invoice: Partial<Invoice>): Promise<void>;

  async function run(config: string | Partial<Invoice>) {
    if (typeof config !== 'string') return process(config);

    await mutation.mutateAsync(config, {
      onSuccess: process,
      onError: (error) => {
        showToast(
          { title: get(error, 'message', 'Unknown error') },
          { type: 'error' },
        );
      },
    });
  }

  return {
    run,
    mutation,
  };
};

export default useExtendRevenueProject;
